import { useMemo } from "react"
import { IconCircleUserOutline18 as UserCircle2Icon } from "nucleo-ui-outline-18"
import { z } from "zod"

import { Button, Select } from "@productlane/ui"

interface ContactStepProps {
	next: () => void
	back: () => void

	contactNameIndex: number
	contactEmailIndex: number

	hasHeaders: boolean
	sortedData: string[][]

	data: string[][]

	setContactNameIndex: (name: number) => void
	setContactEmailIndex: (email: number) => void
}

export function ContactStep({
	back,
	next,
	hasHeaders,
	sortedData,
	contactEmailIndex,
	contactNameIndex,
	setContactEmailIndex,
	setContactNameIndex,
	data,
}: ContactStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		if (hasHeaders) {
			return data[0].map((it, i) => ({
				label: it,
				value: i.toString(),
			}))
		}
		return data[0].map((_, i) => ({
			label: `${i + 1}. Column`,
			value: i.toString(),
		}))
	}, [hasHeaders, data])

	const emails = useMemo(() => {
		if (contactEmailIndex === -1) {
			return []
		}
		const data = () => {
			if (hasHeaders) {
				return sortedData[contactEmailIndex]?.slice(1) ?? []
			}
			return sortedData[contactEmailIndex] ?? []
		}
		return data()
	}, [contactEmailIndex, hasHeaders, sortedData])

	const names = useMemo(() => {
		if (contactNameIndex === -1) {
			return []
		}

		if (hasHeaders) {
			return sortedData[contactNameIndex]?.slice(1) ?? []
		}
		return sortedData[contactNameIndex] ?? []
	}, [contactNameIndex, hasHeaders, sortedData])

	const isEmailValid = useMemo(() => {
		if (contactEmailIndex === -1) {
			return true
		}
		for (const email of emails) {
			if (!checkEmail(email.trim())) {
				return false
			}
		}
		return true
	}, [emails, contactEmailIndex])

	return (
		<div>
			<Select
				value={contactEmailIndex.toString()}
				label="Select the column of the contacts email"
				name="customer-email"
				options={[emptyState("There are no emails"), ...headers]}
				onChange={(it) => setContactEmailIndex(Number(it))}
				showErrorMessage={!isEmailValid}
				errorMessage={isEmailValid ? undefined : "Invalid emails founds"}
			/>

			<div className="relative mt-4">
				{/* {contactEmailIndex === -1 && (
					<div className="absolute inset-0 bg-gray-25 dark:bg-gray-800"></div>
				)} */}

				<Select
					value={contactNameIndex.toString()}
					label="Select the column of the contacts name"
					name="customer-name"
					options={[emptyState("There are no names"), ...headers]}
					onChange={(it) => setContactNameIndex(Number(it))}
				/>
			</div>

			<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
			<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
				{contactEmailIndex === -1 && (
					<div className="text-xs italic text-secondary">No Preview</div>
				)}

				<div className="flex flex-col gap-1">
					{contactEmailIndex !== -1 &&
						emails.slice(0, 3).map((it, i) => (
							<div
								key={`name-${i}`}
								className="flex items-center gap-2 truncate text-sm text-primary"
							>
								<UserCircle2Icon className="h-4 w-4" />
								{names[i]}
								<span
									className={checkEmail(it) ? "text-secondary" : "text-red-500"}
								>
									{`<${it}>`}
								</span>
							</div>
						))}
				</div>
				{contactEmailIndex !== -1 && (
					<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
				)}
			</div>

			<div className="mt-2 flex items-center justify-between">
				<Button variant="link" onClick={back}>
					Back
				</Button>

				<Button onClick={next} disabled={!isEmailValid}>
					Select
				</Button>
			</div>
		</div>
	)
}

const emptyState = (label: string) => ({
	label,
	value: "-1",
})

const emailSchema = z.union([z.literal(""), z.string().email()])

const checkEmail = (email: string) => emailSchema.safeParse(email).success
